import * as React from "react";
import { observer } from "mobx-react";
import { Button, Spin, Popconfirm } from "antd";
import * as firebase from "firebase/app";
import "firebase/auth";
import { useContext, useState } from "react";
import { ContactViewStoreContext } from "../../context/store";
import { Link, RouteComponentProps } from "react-router-dom";
import { title } from "../../style/header";
import { plan2price, i18nSweet } from "../../stores/ContactViewStore";
import { css } from "@emotion/core";
import { ArrowLink } from "../atoms/ArrowLink";
import { medium } from "../../style/button";
import { FixedFooter } from "../atoms/FixedFooter";
import { Footer } from "../organisms/Footer";
import { apiEndpoint } from "../../util";

const formatNumber = (num: number) =>
  num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

const Row = (props: { label: string; amount?: number; price: number }) => (
  <tr>
    <th>{props.label}</th>
    <td
      css={css`
        white-space: nowrap;
      `}
    >
      {props.amount !== undefined && `${props.amount}個`}
    </td>
    <td
      css={css`
        white-space: nowrap;
        ${props.price < 0 ? "color: red;" : ""}
      `}
    >
      {formatNumber(props.price)}円
    </td>
  </tr>
);

export const ApplicationEstimate = observer(
  ({ history }: RouteComponentProps) => {
    const store = useContext(ContactViewStoreContext);

    const [loading, setLoading] = useState(false);

    const planTotal = store.estimate.plans.reduce(
      (sum, plan) => sum + plan2price(plan.plan) * plan.amount,
      0
    );
    const charmTotal = store.estimate.charms.reduce(
      (sum, charm) => sum + charm.charm.price * charm.amount,
      0
    );
    const sweetTotal = store.estimate.sweets.reduce(
      (sum, sweet) =>
        sum +
        ((sweet.sweet.code === "PLAIN"
        ? store.plainBaumDiscountable
        : store.decoBaumDiscountable)
          ? sweet.sweet.price
          : sweet.sweet.listPrice) *
          sweet.amount,
      0
    );
    const tax = Math.floor((planTotal + charmTotal + sweetTotal) * 0.1);

    return (
      <div>
        <h3 css={title}>お見積額確認</h3>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          <div
            css={css`
              box-shadow: 0 1px 4px rgba(102, 29, 48, 0.2);
              border-radius: 20px;
              padding: 12px;
              margin-bottom: 20px;
            `}
          >
            <table
              css={css`
                @media (max-width: 576px) {
                  font-size: 14px;
                }

                @media (max-width: 320px) {
                  font-size: 12px;
                }

                th,
                td {
                  padding: 10px;
                  text-align: left;
                }
              `}
            >
              <tbody>
                {store.estimate.plans
                  .filter(plan => plan.amount > 0)
                  .map(plan => (
                    <Row
                      key={plan.plan}
                      label={plan.plan}
                      amount={plan.amount}
                      price={plan2price(plan.plan) * plan.amount}
                    />
                  ))}
                {store.estimate.charms
                  .filter(charm => charm.amount > 0)
                  .map(x => {
                    console.log(x);
                    return x;
                  })
                  .map(x => (
                    <React.Fragment key={x.charm.name}>
                      <Row
                        label={x.charm.name}
                        amount={x.amount}
                        price={x.charm.listPrice * x.amount}
                      />
                    </React.Fragment>
                  ))}
                {store.estimate.sweets
                  .filter(sweet => sweet.amount > 0)
                  .map(x => (
                    <React.Fragment key={x.sweet.code}>
                      <Row
                        label={i18nSweet(x.sweet.code)}
                        amount={x.amount}
                        price={x.sweet.listPrice * x.amount}
                      />
                      {(x.sweet.code === "PLAIN"
                        ? store.plainBaumDiscountable
                        : store.decoBaumDiscountable) && (
                        <Row
                          label={`　割引`}
                          amount={x.amount}
                          price={
                            -1 * (x.sweet.listPrice - x.sweet.price) * x.amount
                          }
                        />
                      )}
                    </React.Fragment>
                  ))}
                <Row label="消費税" price={tax} />
              </tbody>
              <tfoot
                css={css`
                  font-size: 18px;
                  border-top: solid 1px #ccc;
                `}
              >
                <Row
                  label="お見積り総額"
                  price={planTotal + charmTotal + sweetTotal + tax}
                />
              </tfoot>
            </table>
          </div>
        </div>
        <p
          css={css`
            font-size: 10px;
            color: #999;
          `}
        >
          ※この金額はシステムにより概算として表示されております。
          正式な金額と差異が出る場合がございますのでご了承ください。
        </p>
        <p
          css={css`
            font-size: 10px;
          `}
        >
          商品、個数の調整は2週間前まで可能です。
          申込み後のキャンセルについては、3週間前まで費用がかかりません。
        </p>

        <Footer />

        <FixedFooter>
          <Popconfirm
            title={
              <div>
                お見積内容でPOSTALの申込みを行います。よろしいでしょうか?
                <br />
                お申込み後に数量・プランなど2週間前まで変更できます。
                <br />
                それ以降になりますと変更・キャンセルできません。
                <br />
                ご不明な点等あればお気軽にご相談ください。
              </div>
            }
            disabled={(store.isOrdered && !store.internal) || loading}
            onConfirm={() => {
              const currentUser = firebase.auth().currentUser;
              if (currentUser === null) return;

              setLoading(true);

              store
                .linkCredential()
                .then(_ => store.persist())
                .then(_ =>
                  fetch(apiEndpoint("/postal/orders"), {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json"
                    },
                    body: JSON.stringify(store.toRequest("order"))
                  })
                )
                .then(response => {
                  if (response.status >= 400) throw new Error("server error");
                })
                .then(_ => {
                  store.isOrdered = true;
                  store.persist();
                })
                .then(_ => {
                  history.push("/application/done");
                })
                .catch(_ => {
                  alert("エラーが発生しました");
                  setLoading(false);
                });
            }}
            okText="はい"
            cancelText="いいえ"
          >
            <button
              css={css`
                ${medium}
                ${store.isOrdered && !store.internal && "visibility:hidden;"}
              `}
              disabled={(store.isOrdered && !store.internal) || loading}
            >
              お申込み
              {loading && <Spin size="small" />}
            </button>
          </Popconfirm>
          <ArrowLink to="/application/order" direction="left" />
        </FixedFooter>
      </div>
    );
  }
);
